import { Component, DestroyRef, computed, inject, input, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { CheckboxModule } from 'primeng/checkbox';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { LocalizationService } from '../../../core/localization/localization.service';
import { IdentityAdminService, IdentityRole } from '../../identity/identity-admin.service';
import { AinFormFieldComponent } from '../../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../../shared/design-system/info-hint.component';
import {
  FORM_STOREFRONT_DISPLAY_MODES,
  FormPublicAccess,
  slugifyPublicationSlug
} from '../../../shared/dynamic-form/form-schema.models';

@Component({
  standalone: true,
  selector: 'ain-form-builder-publish-panel',
  imports: [
    FormsModule,
    CheckboxModule,
    InputNumberModule,
    InputTextModule,
    MultiSelectModule,
    SelectModule,
    AinFormFieldComponent,
    InfoHintComponent
  ],
  template: `
    <section class="publish-panel">
      <ain-info-hint
        [titleKey]="'dynamic.builder.publish.infoTitle'"
        [descriptionKey]="'dynamic.builder.publish.infoDescription'"
        [bulletKeys]="[
          'dynamic.builder.publish.infoBulletSchema',
          'dynamic.builder.publish.infoBulletPublication'
        ]" />

      <div class="checkbox-row">
        <p-checkbox
          binary
          [ngModel]="publicAccess().enabled"
          [disabled]="!canEdit()"
          (ngModelChange)="patch({ enabled: $event })" />
        <label>{{ t('dynamic.builder.publish.enabled') }}</label>
      </div>

      <ain-form-field [label]="t('dynamic.builder.publish.slug')">
        <input
          pInputText
          [ngModel]="publicAccess().slug ?? ''"
          [disabled]="!canEdit()"
          (ngModelChange)="updateSlug($event)" />
        @if (slugHint()) {
          <small class="hint">{{ slugHint() }}</small>
        }
      </ain-form-field>

      <div class="checkbox-row">
        <p-checkbox
          binary
          [ngModel]="publicAccess().requireAuth"
          [disabled]="!canEdit()"
          (ngModelChange)="patch({ requireAuth: $event })" />
        <label>{{ t('dynamic.builder.publish.requireAuth') }}</label>
      </div>

      <ain-form-field [label]="t('dynamic.builder.publish.allowedRoles')">
        <p-multiSelect
          [options]="roleOptions()"
          optionLabel="label"
          optionValue="value"
          [ngModel]="publicAccess().allowedRoles ?? []"
          [disabled]="!canEdit()"
          [filter]="true"
          appendTo="body"
          (ngModelChange)="patch({ allowedRoles: $event })" />
      </ain-form-field>

      <h4>{{ t('dynamic.builder.publish.storefront') }}</h4>
      <div class="checkbox-row">
        <p-checkbox
          binary
          [ngModel]="publicAccess().storefront?.publish"
          [disabled]="!canEdit()"
          (ngModelChange)="patchStorefront({ publish: $event })" />
        <label>{{ t('dynamic.builder.publish.storefrontPublish') }}</label>
      </div>

      <ain-form-field [label]="t('dynamic.builder.publish.displayModes')">
        <p-multiSelect
          [options]="displayModeOptions()"
          optionLabel="label"
          optionValue="value"
          [ngModel]="publicAccess().storefront?.displayModes ?? []"
          [disabled]="!canEdit()"
          appendTo="body"
          (ngModelChange)="patchStorefront({ displayModes: $event })" />
      </ain-form-field>

      <h4>{{ t('dynamic.builder.publish.antiAbuse') }}</h4>
      <div class="checkbox-row">
        <p-checkbox
          binary
          [ngModel]="publicAccess().antiAbuse?.captcha"
          [disabled]="!canEdit()"
          (ngModelChange)="patchAntiAbuse({ captcha: $event })" />
        <label>{{ t('dynamic.builder.publish.captcha') }}</label>
      </div>
      <div class="checkbox-row">
        <p-checkbox
          binary
          [ngModel]="publicAccess().antiAbuse?.honeypot"
          [disabled]="!canEdit()"
          (ngModelChange)="patchAntiAbuse({ honeypot: $event })" />
        <label>{{ t('dynamic.builder.publish.honeypot') }}</label>
      </div>
      <ain-form-field [label]="t('dynamic.builder.publish.rateLimitPerMin')">
        <p-inputNumber
          [ngModel]="publicAccess().antiAbuse?.rateLimitPerMin"
          [disabled]="!canEdit()"
          [min]="0"
          [useGrouping]="false"
          (ngModelChange)="patchAntiAbuse({ rateLimitPerMin: $event ?? undefined })" />
      </ain-form-field>

      <div class="checkbox-row">
        <p-checkbox
          binary
          [ngModel]="publicAccess().saveAndResume"
          [disabled]="!canEdit()"
          (ngModelChange)="patch({ saveAndResume: $event })" />
        <label>{{ t('dynamic.builder.publish.saveAndResume') }}</label>
      </div>
    </section>
  `,
  styles: [`
    .publish-panel { display: grid; gap: 12px; padding: 4px 0; }
    .checkbox-row { display: flex; align-items: center; gap: 8px; }
    .hint { color: var(--text-color-secondary); }
    h4 { margin: 8px 0 0; }
  `]
})
export class FormBuilderPublishPanelComponent {
  private readonly localization = inject(LocalizationService);
  private readonly identityApi = inject(IdentityAdminService);
  private readonly destroyRef = inject(DestroyRef);

  readonly publicAccess = input<FormPublicAccess>({});
  readonly canEdit = input(true);
  readonly canPublish = input(true);

  readonly publicAccessChange = output<FormPublicAccess>();
  readonly slugSuggestion = output<string>();

  private roles: IdentityRole[] = [];

  readonly roleOptions = computed(() =>
    this.roles.map(role => ({
      label: role.name,
      value: role.systemName
    }))
  );

  readonly displayModeOptions = computed(() =>
    FORM_STOREFRONT_DISPLAY_MODES.map(value => ({
      label: this.t(`dynamic.builder.publish.displayMode.${value}`),
      value
    }))
  );

  readonly slugHint = computed(() => {
    const slug = slugifyPublicationSlug(this.publicAccess().slug ?? '');
    if (!slug) {
      return '';
    }
    return this.t('dynamic.builder.publish.slugHint').replace('{slug}', slug);
  });

  constructor() {
    this.identityApi.listRoles({ skip: 0, take: 300 })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => this.roles = response.items ?? [],
        error: () => this.roles = []
      });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  updateSlug(value: string): void {
    const slug = slugifyPublicationSlug(value);
    this.patch({ slug });
    if (slug) {
      this.slugSuggestion.emit(slug);
    }
  }

  patch(patch: Partial<FormPublicAccess>): void {
    this.publicAccessChange.emit({ ...(this.publicAccess() ?? {}), ...patch });
  }

  patchStorefront(patch: Partial<NonNullable<FormPublicAccess['storefront']>>): void {
    const current = this.publicAccess() ?? {};
    this.publicAccessChange.emit({
      ...current,
      storefront: { ...(current.storefront ?? {}), ...patch }
    });
  }

  patchAntiAbuse(patch: Partial<NonNullable<FormPublicAccess['antiAbuse']>>): void {
    const current = this.publicAccess() ?? {};
    this.publicAccessChange.emit({
      ...current,
      antiAbuse: { ...(current.antiAbuse ?? {}), ...patch }
    });
  }
}
